// Google's tag (gtag.js), loaded only after the visitor allows it in the
// consent panel. Nothing Google-hosted is fetched before that. One script
// serves two accounts: a Google Analytics 4 property and a Google Ads
// conversion account, each configured only when its id is set. The same
// consent grants ad storage and ad user data. Ad personalisation stays
// denied: the panel does not ask for remarketing.
const COOKIE_DAYS = 180

export const scriptUrl = (id) => `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`

const configured = (ids) => ids.filter(Boolean)

// `document` and `window` are arguments so this runs without a browser.
// `ids` is every account the tag reports to, in any order.
// Returns whether the script was added by this call.
export const loadGoogleAnalytics = ({ document, window, ids = [] }) => {
  const accounts = configured(ids)
  if (accounts.length === 0) return false
  for (const id of accounts) window[`ga-disable-${id}`] = false
  if (window.gtag) {
    window.gtag("consent", "update", { analytics_storage: "granted", ad_storage: "granted", ad_user_data: "granted" })
    return false
  }
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "denied",
  })
  window.gtag("js", new Date())
  for (const id of accounts) {
    window.gtag("config", id, {
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      cookie_expires: COOKIE_DAYS * 86400,
    })
  }
  const script = document.createElement("script")
  script.async = true
  script.src = scriptUrl(accounts[0])
  document.head.appendChild(script)
  return true
}

// Stops further hits and deletes the _ga and _gcl cookies. gtag.js sets them
// on the widest domain it can, so each is expired on the host and every
// parent. Returns the names it cleared.
export const clearGoogleAnalytics = ({ document, window, ids = [], hostname }) => {
  for (const id of configured(ids)) window[`ga-disable-${id}`] = true
  window.gtag?.("consent", "update", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied" })
  const names = document.cookie
    .split(";")
    .map((pair) => pair.split("=")[0].trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_") || name.startsWith("_gcl_"))
  const parts = hostname.split(".")
  const domains = parts.slice(0, -1).map((_, i) => parts.slice(i).join("."))
  for (const name of names) {
    const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    document.cookie = expired
    for (const domain of domains) document.cookie = `${expired}; domain=.${domain}`
  }
  return names
}
